import { styled, keyframes } from "styled-components";

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.1); opacity: 0.8; }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// SVG Animation Keyframes
const floatSvg = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const floatSvg2 = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-15px); }
`;

const floatSvg3 = keyframes`
  0%, 100% { transform: translateY(-5px); }
  50% { transform: translateY(5px); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const pulseSvg = keyframes`
  0%, 100% { opacity: 0.8; r: 4; }
  50% { opacity: 1; r: 6; }
`;

const rotateSvg = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const dash = keyframes`
  to { stroke-dashoffset: -20; }
`;

const particleFloat = keyframes`
  0%, 100% { transform: translateY(0) translateX(0); opacity: 0.6; }
  25% { transform: translateY(-20px) translateX(10px); opacity: 1; }
  50% { transform: translateY(-10px) translateX(-5px); opacity: 0.8; }
  75% { transform: translateY(-30px) translateX(15px); opacity: 1; }
`;

const codeType = keyframes`
  0% { width: 0; }
  100% { width: 100%; }
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(217, 58, 53, 0.3), 0 0 40px rgba(217, 58, 53, 0.1); }
  50% { box-shadow: 0 0 30px rgba(217, 58, 53, 0.5), 0 0 60px rgba(217, 58, 53, 0.2); }
`;

const checkPop = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
`;

const wordReveal = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

export const HeroSectionStyled = styled.section`
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  background: var(--bg-primary);

  /* Bold gradient background */
  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -20%;
    width: 80%;
    height: 150%;
    background: linear-gradient(
      135deg,
      rgba(217, 58, 53, 0.15) 0%,
      rgba(43, 59, 124, 0.2) 50%,
      rgba(217, 58, 53, 0.1) 100%
    );
    background-size: 200% 200%;
    animation: ${gradientMove} 15s ease infinite;
    border-radius: 50%;
    filter: blur(60px);
    pointer-events: none;
  }

  /* Decorative shapes */
  .shape-1 {
    position: absolute;
    top: 10%;
    left: 5%;
    width: 100px;
    height: 100px;
    background: linear-gradient(135deg, var(--accent) 0%, rgba(217, 58, 53, 0.6) 100%);
    border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
    animation: ${float} 6s ease-in-out infinite;
    opacity: 0.7;
  }

  .shape-2 {
    position: absolute;
    bottom: 15%;
    left: 10%;
    width: 60px;
    height: 60px;
    background: var(--primary);
    border-radius: 50%;
    animation: ${pulse} 4s ease-in-out infinite;
  }

  .shape-3 {
    position: absolute;
    top: 20%;
    right: 10%;
    width: 150px;
    height: 150px;
    border: 3px solid var(--accent);
    border-radius: 50%;
    animation: ${float} 8s ease-in-out infinite reverse;
    opacity: 0.5;
  }

  .shape-4 {
    position: absolute;
    bottom: 25%;
    right: 5%;
    width: 80px;
    height: 80px;
    background: linear-gradient(45deg, var(--primary), transparent);
    transform: rotate(45deg);
    animation: ${float} 5s ease-in-out infinite;
    opacity: 0.4;
  }

  .heroSectionContainer {
    position: relative;
    max-width: 1400px;
    width: 100%;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 60px;
    padding: 120px 40px;
    align-items: center;
    z-index: 1;
  }

  .heroContent {
    position: relative;

    .tagline {
      display: inline-flex;
      align-items: center;
      gap: 16px;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
      backdrop-filter: blur(10px);
      padding: 10px 24px 10px 10px;
      border-radius: 60px;
      margin-bottom: 35px;
      border: 1px solid rgba(217, 58, 53, 0.3);
      animation: ${glow} 3s ease-in-out infinite;
      position: relative;
      overflow: hidden;

      .badgeWrapper {
        position: relative;
        overflow: hidden;
        border-radius: 50px;

        .shimmer {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.4),
            transparent
          );
          animation: ${shimmer} 3s infinite;
        }
      }

      .hpBadge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: linear-gradient(135deg, var(--accent) 0%, #b52a26 100%);
        color: #fff;
        padding: 12px 18px;
        border-radius: 50px;
        font-family: Poppins;
        position: relative;
        z-index: 1;

        .badgeIcon {
          position: relative;
          width: 24px;
          height: 24px;

          svg {
            position: absolute;
            top: 0;
            left: 0;
            width: 24px;
            height: 24px;

            &.checkIcon {
              animation: ${checkPop} 0.5s ease-out forwards;
              animation-delay: 0.8s;
              opacity: 0;
            }
          }
        }

        .badgeText {
          font-size: 16px;
          font-weight: 800;
          letter-spacing: 2px;
        }
      }

      .taglineDivider {
        width: 2px;
        height: 30px;
        background: linear-gradient(180deg, transparent, var(--accent), transparent);
        border-radius: 2px;
      }

      .taglineText {
        display: flex;
        gap: 8px;
        font-family: Poppins;
        font-size: 13px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 2px;

        .word {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: var(--text-primary);
          animation: ${wordReveal} 0.5s ease-out forwards;
          opacity: 0;

          svg {
            width: 14px;
            height: 14px;
            stroke-width: 2.5;
          }

          &:nth-child(1) {
            animation-delay: 0.3s;
            svg { color: var(--accent); }
          }
          &:nth-child(2) {
            animation-delay: 0.5s;
            background: linear-gradient(135deg, var(--accent) 0%, var(--primary) 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            svg {
              color: var(--primary);
              -webkit-text-fill-color: initial;
            }
          }
          &:nth-child(3) {
            animation-delay: 0.7s;
            svg { color: var(--accent); }
          }
        }
      }

      .verifiedIcon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        background: linear-gradient(135deg, var(--primary, #2b3b7c) 0%, rgba(43, 59, 124, 0.8) 100%);
        border-radius: 50%;
        color: #fff;
        animation: ${checkPop} 0.6s ease-out forwards;
        animation-delay: 1s;
        opacity: 0;

        svg {
          width: 16px;
          height: 16px;
        }
      }

      .badgeContact {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-left: 16px;
        padding-left: 16px;
        border-left: 1px solid rgba(217, 58, 53, 0.3);

        .badgeContactIcon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: rgba(217, 58, 53, 0.15);
          border: 1px solid rgba(217, 58, 53, 0.25);
          text-decoration: none;
          transition: all 0.3s ease;

          svg {
            width: 18px;
            height: 18px;
            color: var(--accent);
            stroke-width: 2.5;
          }

          &:hover {
            background: var(--accent);
            border-color: var(--accent);
            transform: translateY(-2px);

            svg {
              color: #fff;
            }
          }
        }
      }
    }

    .heroContact {
      display: flex;
      align-items: center;
      gap: 20px;
      margin-top: 30px;
      flex-wrap: wrap;

      .contactLink {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        text-decoration: none;
        color: var(--text-secondary);
        font-family: Poppins;
        font-size: 18px;
        font-weight: 600;
        transition: all 0.3s ease;
        padding: 8px 0;

        &:hover {
          color: var(--accent);

          .contactIcon {
            background: var(--accent);
            color: #fff;
          }
        }

        .contactIcon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 42px;
          height: 42px;
          border-radius: 12px;
          background: rgba(217, 58, 53, 0.25);
          color: var(--accent);
          transition: all 0.3s ease;
          border: 1px solid rgba(217, 58, 53, 0.3);

          svg {
            width: 20px;
            height: 20px;
            stroke-width: 2.5;
          }
        }

        .contactText {
          transition: color 0.3s ease;
        }
      }

      .contactDivider {
        color: var(--card-border);
        font-weight: 300;
      }
    }
  }

  .heroTitle {
    font-family: Poppins;
    font-size: 72px;
    font-weight: 800;
    line-height: 1.05;
    margin-bottom: 30px;
    color: var(--text-primary);

    .highlight {
      background: linear-gradient(135deg, var(--accent) 0%, rgba(217, 58, 53, 0.8) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .word {
      display: inline-block;
      will-change: transform, opacity;
    }
  }

  .heroParagraph {
    font-family: Poppins;
    font-size: 20px;
    line-height: 1.8;
    color: var(--text-secondary);
    margin-bottom: 40px;
    max-width: 500px;
  }

  .heroCTA {
    display: flex;
    gap: 20px;
    align-items: center;

    .primaryBtn {
      display: inline-flex;
      align-items: center;
      gap: 12px;
      padding: 18px 36px;
      background: linear-gradient(135deg, var(--accent) 0%, #c12f2a 100%);
      color: #fff;
      font-family: Poppins;
      font-size: 16px;
      font-weight: 600;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
      text-decoration: none;
      box-shadow: 0 10px 30px rgba(217, 58, 53, 0.3);

      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 40px rgba(217, 58, 53, 0.4);
      }

      svg {
        width: 20px;
        height: 20px;
        transition: transform 0.3s ease;
      }

      &:hover svg {
        transform: translateX(5px);
      }
    }

    .secondaryBtn {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 18px 36px;
      background: transparent;
      color: var(--text-primary);
      font-family: Poppins;
      font-size: 16px;
      font-weight: 600;
      border: 2px solid var(--card-border);
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
      text-decoration: none;

      &:hover {
        border-color: var(--primary);
        background: var(--bg-secondary);
      }
    }
  }

  .imageContainer {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    .imageWrapper {
      position: relative;
      z-index: 2;
      width: 100%;
      max-width: 580px;

      /* Hero Image Grid Layout */
      .heroImageGrid {
        position: relative;
        display: grid;
        grid-template-columns: 1.2fr 1fr;
        grid-template-rows: auto auto;
        gap: 20px;
        padding: 20px;

        .mainImage {
          grid-row: 1 / 3;
          position: relative;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 30px 60px var(--shadow-large);

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.6s ease;
          }

          &:hover img {
            transform: scale(1.05);
          }

          .imageOverlay {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 24px;
            background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.8) 100%);

            .overlayBadge {
              display: inline-flex;
              align-items: center;
              gap: 10px;
              background: var(--accent);
              color: #fff;
              padding: 10px 18px;
              border-radius: 50px;
              font-family: Poppins;
              font-size: 14px;
              font-weight: 600;

              svg {
                width: 18px;
                height: 18px;
              }
            }
          }
        }

        .secondaryImages {
          display: flex;
          flex-direction: column;
          gap: 16px;

          .imageCard {
            position: relative;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 15px 40px var(--shadow-medium);
            transition: all 0.4s ease;

            img {
              width: 100%;
              height: 100px;
              object-fit: cover;
              transition: transform 0.5s ease;
            }

            &:hover {
              transform: translateY(-5px) scale(1.02);
              box-shadow: 0 20px 50px var(--shadow-large);

              img {
                transform: scale(1.1);
              }
            }

            .cardLabel {
              position: absolute;
              bottom: 0;
              left: 0;
              right: 0;
              padding: 12px 16px;
              background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.85) 100%);
              display: flex;
              align-items: center;
              gap: 8px;
              color: #fff;
              font-family: Poppins;
              font-size: 13px;
              font-weight: 600;

              svg {
                width: 16px;
                height: 16px;
              }
            }

            &.card1 {
              animation: ${floatSvg} 6s ease-in-out infinite;
            }

            &.card2 {
              animation: ${floatSvg2} 7s ease-in-out infinite;
            }

            &.card3 {
              animation: ${floatSvg3} 5s ease-in-out infinite;
            }
          }
        }

        .floatingBadge {
          position: absolute;
          bottom: -10px;
          left: -20px;
          background: linear-gradient(135deg, var(--accent) 0%, #b52a26 100%);
          color: #fff;
          padding: 20px 28px;
          border-radius: 16px;
          box-shadow: 0 20px 50px rgba(217, 58, 53, 0.4);
          animation: ${float} 4s ease-in-out infinite;
          z-index: 10;

          .badgeNumber {
            font-family: Poppins;
            font-size: 36px;
            font-weight: 800;
            line-height: 1;
          }

          .badgeLabel {
            font-family: Poppins;
            font-size: 12px;
            font-weight: 500;
            opacity: 0.9;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
        }

        .decorDot {
          position: absolute;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: var(--accent);

          &.dot1 {
            top: 0;
            right: 30%;
            animation: ${pulse} 2s ease-in-out infinite;
          }

          &.dot2 {
            bottom: 20%;
            right: -10px;
            background: var(--primary);
            animation: ${pulse} 2.5s ease-in-out infinite 0.5s;
          }
        }

        .decorCircle {
          position: absolute;
          top: -30px;
          right: -30px;
          width: 100px;
          height: 100px;
          border: 3px solid var(--accent);
          border-radius: 50%;
          opacity: 0.3;
          animation: ${float} 8s ease-in-out infinite reverse;
        }
      }
    }

    .imageBg {
      position: absolute;
      width: 120%;
      height: 120%;
      background: linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%);
      border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
      opacity: 0.1;
      animation: ${float} 10s ease-in-out infinite;
    }
  }

  .scrollIndicator {
    position: absolute;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    color: var(--text-muted);
    font-family: Poppins;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 2px;

    .mouse {
      width: 24px;
      height: 40px;
      border: 2px solid var(--text-muted);
      border-radius: 12px;
      position: relative;

      &::after {
        content: "";
        position: absolute;
        top: 8px;
        left: 50%;
        transform: translateX(-50%);
        width: 4px;
        height: 8px;
        background: var(--accent);
        border-radius: 2px;
        animation: ${float} 1.5s ease-in-out infinite;
      }
    }
  }

  @media (max-width: 1024px) {
    .heroSectionContainer {
      grid-template-columns: 1fr;
      text-align: center;
      padding: 100px 30px;
    }

    .heroContent {
      .tagline {
        justify-content: center;
        flex-wrap: wrap;
        gap: 12px;
        padding: 12px 16px;

        .taglineDivider {
          display: none;
        }

        .taglineText {
          .word {
            font-size: 12px;
          }
        }

        .badgeContact {
          display: none;
        }
      }
    }

    .heroTitle {
      font-size: 52px;
    }

    .heroParagraph {
      margin: 0 auto 40px;
    }

    .heroCTA {
      justify-content: center;
      flex-wrap: wrap;
    }

    .heroContact {
      justify-content: center;
    }

    .imageContainer {
      display: none;
    }

    .shape-1, .shape-3 {
      display: none;
    }
  }

  @media (max-width: 768px) {
    .heroSectionContainer {
      padding: 100px 20px 80px;
    }

    .heroContent {
      .tagline {
        flex-direction: column;
        gap: 10px;
        padding: 12px 16px;
        border-radius: 20px;

        .badgeWrapper {
          .hpBadge {
            padding: 10px 16px;

            .badgeIcon {
              width: 20px;
              height: 20px;

              svg {
                width: 20px;
                height: 20px;
              }
            }

            .badgeText {
              font-size: 14px;
            }
          }
        }

        .taglineDivider {
          display: none;
        }

        .taglineText {
          flex-wrap: wrap;
          justify-content: center;
          gap: 6px;

          .word {
            font-size: 11px;
            letter-spacing: 1px;
          }
        }

        .verifiedIcon {
          width: 24px;
          height: 24px;

          svg {
            width: 14px;
            height: 14px;
          }
        }
      }
    }

    .heroTitle {
      font-size: 42px;
    }

    .heroParagraph {
      font-size: 16px;
    }
  }

  @media (max-width: 640px) {
    .heroSectionContainer {
      padding: 90px 16px 60px;
    }

    .heroContent {
      .tagline {
        width: 100%;
        max-width: 320px;
        margin: 0 auto 25px;
        padding: 10px 14px;
        gap: 8px;

        .badgeWrapper {
          .hpBadge {
            padding: 8px 14px;

            .badgeIcon {
              width: 18px;
              height: 18px;

              svg {
                width: 18px;
                height: 18px;
              }
            }

            .badgeText {
              font-size: 13px;
              letter-spacing: 1px;
            }
          }
        }

        .taglineText {
          gap: 5px;

          .word {
            font-size: 10px;
            letter-spacing: 0.5px;
          }
        }

        .verifiedIcon {
          width: 22px;
          height: 22px;
          position: absolute;
          top: -8px;
          right: -8px;

          svg {
            width: 12px;
            height: 12px;
          }
        }
      }
    }

    .heroTitle {
      font-size: 32px;
      line-height: 1.2;
      margin-bottom: 20px;
    }

    .heroParagraph {
      font-size: 15px;
      line-height: 1.7;
      margin-bottom: 30px;
    }

    .heroCTA {
      flex-direction: column;
      gap: 12px;

      .primaryBtn, .secondaryBtn {
        width: 100%;
        justify-content: center;
        padding: 16px 28px;
        font-size: 15px;
      }
    }

    .heroContact {
      flex-direction: column;
      gap: 12px;
      margin-top: 24px;

      .contactDivider {
        display: none;
      }

      .contactLink {
        font-size: 15px;

        .contactIcon {
          width: 36px;
          height: 36px;

          svg {
            width: 18px;
            height: 18px;
          }
        }
      }
    }

    .shape-2, .shape-4 {
      display: none;
    }

    .scrollIndicator {
      bottom: 20px;
    }
  }

  @media (max-width: 400px) {
    .heroContent {
      .tagline {
        max-width: 280px;

        .taglineText {
          .word {
            font-size: 9px;
          }
        }
      }
    }

    .heroTitle {
      font-size: 28px;
    }
  }
`;
